/*
 * cdx-utils.js
 * Shared helpers for the cdx-* components (window.CdxUtils), the global
 * in-page anchor scrolling with sticky-header offset and [data-klaviyo-form] buttons.
 * Loaded once in the layout <head> with defer, right after scripts.js.
 */
if (!window.CdxUtils) {
  window.CdxUtils = (() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    // Height of the (sticky) header that covers the top of the viewport.
    const getHeaderOffset = () => {
      const header =
        document.querySelector("sticky-header") ||
        document.querySelector(".header-wrapper") ||
        document.querySelector("header") ||
        document.querySelector(".header");
      return header ? header.offsetHeight : 0;
    };

    // Scrolls so the element sits below the header. `readjust` re-runs the scroll
    // after the given delays (ms) for content that still shifts (e.g. opening accordions).
    const scrollToElementWithOffset = (
      element,
      { extra = 20, includeHeader = true, readjust = [] } = {}
    ) => {
      if (!element) return;
      const scroll = () => {
        const offset = (includeHeader ? getHeaderOffset() : 0) + extra;
        const top = element.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: reducedMotion.matches ? "auto" : "smooth",
        });
      };
      scroll();
      readjust.forEach((delay) => setTimeout(scroll, delay));
    };

    const findAnchorTarget = (hash) => {
      const id = hash.slice(1);
      let target = document.getElementById(id);
      if (!target) {
        try {
          target = document.getElementById(decodeURIComponent(id));
        } catch (error) {
          target = null;
        }
      }
      return target;
    };

    // RTE content puts ids on links inside headings; move them to the heading
    // so the scroll target is the heading itself.
    document
      .querySelectorAll("h1 a[id], h2 a[id], h3 a[id], h4 a[id], h5 a[id], h6 a[id]")
      .forEach((anchor) => {
        anchor.parentElement.id = anchor.id;
        anchor.removeAttribute("id");
      });

    // One delegated listener, so links added later (Ajax, drawers) work too.
    // The skip link keeps its native behaviour (moves focus into <main>).
    document.addEventListener("click", (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const link = event.target.closest('a[href^="#"]');
      if (!link || link.matches(".skip-link, [data-no-smooth-scroll]")) return;

      const hash = link.getAttribute("href");
      if (hash.length < 2 || hash === "#anfang") return;

      const target = findAnchorTarget(hash);
      if (!target) return;

      event.preventDefault();
      scrollToElementWithOffset(target);

      // Keyboard activation (detail 0): move focus to the target as well.
      if (event.detail === 0) {
        if (!target.matches("a[href], button, input, select, textarea, [tabindex]")) {
          target.setAttribute("tabindex", "-1");
        }
        target.focus({ preventScroll: true });
      }
    });

    // Buttons that open a Klaviyo onsite form: <button type="button" data-klaviyo-form="FORM_ID">
    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-klaviyo-form]");
      if (!trigger) return;
      event.preventDefault();
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(["openForm", trigger.dataset.klaviyoForm]);
    });

    return { getHeaderOffset, scrollToElementWithOffset };
  })();
}
